import type { Object3D } from 'three';
import type { SketchGroup } from '../../../../mesh/sketch/SketchGroup';
import type { ISketchObject } from '../../../../mesh/sketch/ISketchObject';
import { SketchReferenceEntityFactory } from '../../target/SketchReferenceEntityFactory';
import { resolveSourceDisplayLabel, resolveStableSourceDisplayLabel } from '../../model/EdgeLabelUtils';
import {
	resolveExternalGeometrySourceDescriptorKey,
	resolveExternalGeometrySourceEdgeDisplayIndex,
	type ExternalGeometrySourceDescriptor,
} from '../../model/ExternalGeometrySourceDescriptor';
import {
	buildSketchEntityReferenceRecord,
	type SketchEntityReferenceRecord,
} from '../../model/SketchEntityReferenceRecord';
import { createExecutionDiagnostic, type ExecutionDiagnostic } from '../../model/SketchEntityReferenceDiagnostics';
import { createSketchEntityReferenceId, writeSketchEntityReferenceTag, type SketchEntityReferenceTag } from '../../model/SketchEntityReferenceIds';
import type { CreateSketchEntityReferencesResult } from '../../model/SketchEntityReferenceResults';
import type { CreateGeometryReferencesInput } from '../../model/SketchEntityReferenceTypes';
import type {
	SketchEntityReferenceRepository,
	SketchEntityReferenceRepositoryWriteScope,
} from '../../repository/SketchEntityReferenceRepository';
import type {SketchEntityReferenceRebuildTransactionPort} from '../../../../../types/reference/sketch-entity-reference/SketchEntityReferenceRebuildBatchTransaction';
import { SketchReferenceRelationAttachDispatcher } from '../../relation/SketchReferenceRelationAttachDispatcher';
import { SketchEntityReferenceObjectCleanupService } from '../../target/SketchEntityReferenceObjectCleanupService';
import {
	SketchEntityReferenceProjectionService,
	type SketchEntityReferenceProjectionPort,
} from '../../source/projection/SketchEntityReferenceProjectionService';
import { SketchBasicConstraintRestoreService } from '../../relation/SketchBasicConstraintRestoreService';
import { SketchEntityReferenceCycleGuard } from './SketchEntityReferenceCycleGuard';
import {executeSketchEntityReferenceRepositoryWrite} from '../../utils/executeSketchEntityReferenceRepositoryWrite';
import type {
	CreateGeometryReferencesAttachedRelationRecord,
	CreateGeometryReferencesCommandServiceDependencies,
	CreateGeometryReferencesCommandServiceRuntime,
} from '../../../../../types/reference/sketch-entity-reference/CreateGeometryReferencesCommandService';

export type {
	CreateGeometryReferencesCommandServiceDependencies,
	CreateGeometryReferencesCommandServiceRuntime,
} from '../../../../../types/reference/sketch-entity-reference/CreateGeometryReferencesCommandService';

/**
 * 解析来源对象的运行时名称。
 *
 * @param sourceDescriptor 来源描述。
 * @param runtime 运行时依赖。
 * @returns 来源名称；不可解析时返回 null。
 */
function resolveRuntimeSourceName(sourceDescriptor: ExternalGeometrySourceDescriptor, runtime: CreateGeometryReferencesCommandServiceRuntime): string | null {
	if (sourceDescriptor.kind !== 'sketch-entity') {
		return null;
	}

	return runtime.getSketchObject(sourceDescriptor.sourceSketchId, sourceDescriptor.entityId)?.name ?? null;
}

/**
 * 解析来源显示标签。
 *
 * @param sourceDescriptor 来源描述。
 * @param runtime 运行时依赖。
 * @returns 可展示标签。
 */
function resolveReferenceSourceDisplayLabel(
	sourceDescriptor: ExternalGeometrySourceDescriptor,
	runtime: CreateGeometryReferencesCommandServiceRuntime,
	sourceDisplayLabelsByKey: Record<string, string> | undefined
): string {
	const sourceKey = resolveExternalGeometrySourceDescriptorKey(sourceDescriptor);
	const sessionDisplayLabel = sourceDisplayLabelsByKey?.[sourceKey]?.trim();
	if (sessionDisplayLabel) {
		return resolveStableSourceDisplayLabel(sessionDisplayLabel);
	}

	if (sourceDescriptor.kind === 'sketch-entity') {
		return resolveSourceDisplayLabel(runtime.getSketchObject(sourceDescriptor.sourceSketchId, sourceDescriptor.entityId)?.name ?? '');
	}

	const edgeDisplayIndex = resolveExternalGeometrySourceEdgeDisplayIndex(sourceDescriptor);
	return edgeDisplayIndex === null ? '外部边线' : `边线${edgeDisplayIndex}`;
}

/**
 * 解析来源对应的用户选择意图快照。
 *
 * @param sourceDescriptor 来源描述。
 * @param selectionIntentsBySourceKey create input 中的选择意图映射。
 * @returns 命中时返回可写入引用记录的选择意图快照。
 */
function resolveReferenceSelectionIntent(
	sourceDescriptor: ExternalGeometrySourceDescriptor,
	selectionIntentsBySourceKey: CreateGeometryReferencesInput['selectionIntentsBySourceKey']
): NonNullable<CreateGeometryReferencesInput['selectionIntentsBySourceKey']>[string] | undefined {
	const sourceKey = resolveExternalGeometrySourceDescriptorKey(sourceDescriptor);
	return selectionIntentsBySourceKey?.[sourceKey];
}

/**
 * 创建几何引用命令服务。
 * 说明：
 * 1. 当前实现接入正式 create 主链：来源投影归一化 -> 创建目标草图实体 -> 恢复基础重合 -> 挂载引用约束 -> 保存引用记录；
 * 2. 来源既可以是草图实体，也可以是模型/特征 topology 边；最终创建结果只取决于来源投影后的几何类型；
 * 3. sketch-entity 来源会额外执行循环引用检测，topology-edge 来源不参与草图间 cycle guard。
 */
export class CreateGeometryReferencesCommandService {
	private readonly entityFactory: SketchReferenceEntityFactory;
	private readonly repository: SketchEntityReferenceRepository;
	private readonly relationAttachDispatcher: SketchReferenceRelationAttachDispatcher;
	private readonly basicConstraintRestoreService: SketchBasicConstraintRestoreService;
	private readonly cycleGuard: SketchEntityReferenceCycleGuard;
	private readonly objectCleanupService: SketchEntityReferenceObjectCleanupService;
	private readonly projectionService: SketchEntityReferenceProjectionPort | null;

	/**
	 * @param dependencies 服务依赖。
	 */
	public constructor(private readonly dependencies: CreateGeometryReferencesCommandServiceDependencies) {
		if (!dependencies.repository) {
			throw new Error('创建转换实体引用服务必须提供当前 Viewer 的引用仓库');
		}
		if (!dependencies.solveTargetSketchParametricState) {
			throw new Error('创建转换实体引用服务必须提供统一参数求解端口');
		}

		this.entityFactory =
			dependencies.entityFactory ??
			new SketchReferenceEntityFactory({
				lineReferenceBuilder: dependencies.lineReferenceBuilder,
				circleReferenceBuilder: dependencies.circleReferenceBuilder,
				arcReferenceBuilder: dependencies.arcReferenceBuilder,
				ellipseReferenceBuilder: dependencies.ellipseReferenceBuilder,
				ellipseArcReferenceBuilder: dependencies.ellipseArcReferenceBuilder,
				splineReferenceBuilder: dependencies.splineReferenceBuilder,
				styleSplineReferenceBuilder: dependencies.styleSplineReferenceBuilder,
				objectCleanupService: dependencies.objectCleanupService,
			});
		this.repository = dependencies.repository;
		this.relationAttachDispatcher = dependencies.relationAttachDispatcher ?? new SketchReferenceRelationAttachDispatcher();
		this.basicConstraintRestoreService = dependencies.basicConstraintRestoreService ?? new SketchBasicConstraintRestoreService();
		this.cycleGuard =
			dependencies.cycleGuard ??
			new SketchEntityReferenceCycleGuard({
				repository: this.repository,
			});
		this.objectCleanupService = dependencies.objectCleanupService ?? new SketchEntityReferenceObjectCleanupService();
		this.projectionService = dependencies.runtime
			? (dependencies.projectionService ??
				new SketchEntityReferenceProjectionService({
					runtime: dependencies.runtime,
					sourceResolver: dependencies.sourceResolver,
					lineProjectionPreprocessor: dependencies.lineProjectionPreprocessor,
					circleProjectionPreprocessor: dependencies.circleProjectionPreprocessor,
					arcProjectionPreprocessor: dependencies.arcProjectionPreprocessor,
					ellipseProjectionPreprocessor: dependencies.ellipseProjectionPreprocessor,
					ellipseArcProjectionPreprocessor: dependencies.ellipseArcProjectionPreprocessor,
					parabolaProjectionPreprocessor: dependencies.parabolaProjectionPreprocessor,
					splineProjectionPreprocessor: dependencies.splineProjectionPreprocessor,
					styleSplineProjectionPreprocessor: dependencies.styleSplineProjectionPreprocessor,
				}))
			: null;
	}

	/**
	 * 执行创建几何引用命令。
	 *
	 * @param input 创建输入。
	 * @returns 创建结果。
	 */
	public async execute(input: CreateGeometryReferencesInput): Promise<CreateSketchEntityReferencesResult> {
		return this.executeInternal(input);
	}

	/**
	 * 在阶段 B 的定义变更归属下创建派生面成员。
	 *
	 * 该入口与普通创建共享完整事务，只额外标记草图 dirty 的来源，避免把同一 checkpoint
	 * 内部新增边误判为外部并发编辑。
	 *
	 * @param input 标准 Convert Entities 创建输入。
	 * @param definitionChangeOwnerId 阶段 B 传播 ID。
	 * @param isInputCurrent 当前 checkpoint 是否仍拥有正式状态提交权。
	 * @returns 创建结果。
	 */
	public async executeDerived(
		input: CreateGeometryReferencesInput,
		definitionChangeOwnerId: string,
		isInputCurrent: () => boolean = () => true,
		repository: SketchEntityReferenceRepositoryWriteScope = this.repository,
		transaction?: SketchEntityReferenceRebuildTransactionPort,
		batchKey: string = input.targetSketchId,
	): Promise<CreateSketchEntityReferencesResult> {
		return this.executeInternal(
			input,
			definitionChangeOwnerId,
			isInputCurrent,
			repository,
			transaction,
			batchKey,
			true,
		);
	}

	/**
	 * 执行普通创建或阶段 B 派生创建的共用事务。
	 *
	 * @param input 创建输入。
	 * @param definitionChangeOwnerId 可选的内部定义变更归属。
	 * @param isInputCurrent 当前输入是否仍允许提交正式状态。
	 * @param deferBatchConstraintResolution 是否由外层重建批次统一恢复基础关系并执行参数求解。
	 * @returns 创建结果。
	 */
	private async executeInternal(
		input: CreateGeometryReferencesInput,
		definitionChangeOwnerId?: string,
		isInputCurrent: () => boolean = () => true,
		repository: SketchEntityReferenceRepositoryWriteScope = this.repository,
		transaction?: SketchEntityReferenceRebuildTransactionPort,
		batchKey: string = input.targetSketchId,
		deferBatchConstraintResolution = false,
	): Promise<CreateSketchEntityReferencesResult> {
		if (!isInputCurrent()) {
			return this.createEmptyResult();
		}
		if (!this.dependencies.runtime) {
			return {
				createdReferences: [],
				createdReferenceIds: [],
				createdTargetEntityIds: [],
				diagnostics: [
					createExecutionDiagnostic({
						kind: 'input-invalid',
						level: 'error',
						message: 'CreateGeometryReferencesCommandService 缺少 runtime，无法执行创建链路。',
					}),
				],
			};
		}

		const runtime = this.dependencies.runtime;
		const targetSketch = runtime.getSketchGroup(input.targetSketchId);
		if (!targetSketch) {
			return {
				createdReferences: [],
				createdReferenceIds: [],
				createdTargetEntityIds: [],
				diagnostics: [
					createExecutionDiagnostic({
						kind: 'input-invalid',
						level: 'error',
						message: `未找到目标草图，无法创建几何引用: ${input.targetSketchId}`,
					}),
				],
			};
		}

		const diagnostics: ExecutionDiagnostic[] = [];
		const createdEntities: Array<Object3D & ISketchObject> = [];
		const createdReferences: SketchEntityReferenceRecord[] = [];
		const attachedRelations: Array<{
			reference: SketchEntityReferenceRecord;
			targetEntity: Object3D;
			record: CreateGeometryReferencesAttachedRelationRecord;
		}> = [];

		try {
			const normalizedProjectionResults = this.projectionService!.project({
				targetSketch,
				sourceDescriptors: input.sources,
				runtimeTopologySourcesBySourceKey: input.runtimeTopologySourcesBySourceKey,
			});
			diagnostics.push(...normalizedProjectionResults.diagnostics);

			const factoryResult = this.runDefinitionChangeScope(
				targetSketch,
				definitionChangeOwnerId,
				() => this.entityFactory.createBatch({
					targetSketch,
					projectionResults: normalizedProjectionResults.projectionResults,
					options: input.options,
					resolveSourceObject: sourceDescriptor =>
						sourceDescriptor.kind === 'sketch-entity' ? runtime.getSketchObject(sourceDescriptor.sourceSketchId, sourceDescriptor.entityId) : null,
				}),
			);
			diagnostics.push(...factoryResult.diagnostics);
			createdEntities.push(...factoryResult.createdEntities);
			if (!isInputCurrent()) {
				this.runDefinitionChangeScope(targetSketch, definitionChangeOwnerId, () => {
					this.objectCleanupService.cleanupTargetEntities(createdEntities);
				});
				return this.createEmptyResult();
			}

			if (diagnostics.some(diagnostic => diagnostic.level === 'error')) {
				this.runDefinitionChangeScope(targetSketch, definitionChangeOwnerId, () => {
					this.objectCleanupService.cleanupTargetEntities(createdEntities);
				});
				return {
					createdReferences: [],
					createdReferenceIds: [],
					createdTargetEntityIds: [],
					diagnostics,
				};
			}

			const preparedReferenceTargets = factoryResult.createdItems.map(createdItem => ({
				createdItem,
				runtimeSourceName: resolveRuntimeSourceName(createdItem.projectionResult.sourceDescriptor, runtime) ?? undefined,
				reference: buildSketchEntityReferenceRecord({
					referenceId: createSketchEntityReferenceId(),
					projectionResult: createdItem.projectionResult,
					targetSketchId: input.targetSketchId,
					targetEntityId: createdItem.targetEntity.persistentId,
					sourceDisplayLabel: resolveReferenceSourceDisplayLabel(
						createdItem.projectionResult.sourceDescriptor,
						runtime,
						input.sourceDisplayLabelsByKey
					),
					selectionIntent: resolveReferenceSelectionIntent(createdItem.projectionResult.sourceDescriptor, input.selectionIntentsBySourceKey),
				}),
			}));

			if (!deferBatchConstraintResolution) {
				const restoreResult = this.runDefinitionChangeScope(
					targetSketch,
					definitionChangeOwnerId,
					() => this.basicConstraintRestoreService.restore({
						sourceDescriptors: input.sources,
						options: input.options,
						runtime,
						targetAnchorBindings: factoryResult.targetAnchorBindings,
					}),
				);
				diagnostics.push(...restoreResult.diagnostics);
				if (restoreResult.diagnostics.some(diagnostic => diagnostic.level === 'error')) {
					this.runDefinitionChangeScope(targetSketch, definitionChangeOwnerId, () => {
						this.objectCleanupService.cleanupTargetEntities(createdEntities);
					});
					return {
						createdReferences: [],
						createdReferenceIds: [],
						createdTargetEntityIds: [],
						diagnostics,
					};
				}
			}

			for (const preparedReferenceTarget of preparedReferenceTargets) {
				const sourceDescriptor = preparedReferenceTarget.createdItem.projectionResult.sourceDescriptor;
				if (sourceDescriptor.kind === 'sketch-entity') {
					const cycleCheck = await this.cycleGuard.check({
						sourceSketchId: sourceDescriptor.sourceSketchId,
						sourceEntityId: sourceDescriptor.entityId,
						targetSketchId: input.targetSketchId,
						targetEntityId: preparedReferenceTarget.reference.targetEntityId,
					}, repository);
					if (!isInputCurrent()) {
						this.runDefinitionChangeScope(targetSketch, definitionChangeOwnerId, () => {
							this.rollbackAttachedRelations(attachedRelations);
							this.objectCleanupService.cleanupTargetEntities(createdEntities);
						});
						return this.createEmptyResult();
					}
					if (!cycleCheck.allowed) {
						if (cycleCheck.diagnostic) {
							diagnostics.push(cycleCheck.diagnostic);
						}
						this.runDefinitionChangeScope(targetSketch, definitionChangeOwnerId, () => {
							this.rollbackAttachedRelations(attachedRelations);
							this.objectCleanupService.cleanupTargetEntities(createdEntities);
						});
						return {
							createdReferences: [],
							createdReferenceIds: [],
							createdTargetEntityIds: [],
							diagnostics,
						};
					}
				}

				const attachResult = this.runDefinitionChangeScope(
					targetSketch,
					definitionChangeOwnerId,
					() => this.relationAttachDispatcher.attach({
						reference: preparedReferenceTarget.reference,
						projectionResult: preparedReferenceTarget.createdItem.projectionResult,
						targetEntity: preparedReferenceTarget.createdItem.targetEntity,
						runtimeSourceName: preparedReferenceTarget.runtimeSourceName,
					}),
				);
				diagnostics.push(...attachResult.diagnostics);
				if (attachResult.diagnostics.some(diagnostic => diagnostic.level === 'error') || !attachResult.record) {
					if (!attachResult.record) {
						diagnostics.push(
							createExecutionDiagnostic({
								kind: 'restore-failed',
								level: 'error',
								message: `引用关系挂载未返回运行时记录: ${preparedReferenceTarget.reference.referenceId}`,
								referenceId: preparedReferenceTarget.reference.referenceId,
								targetEntityId: preparedReferenceTarget.reference.targetEntityId,
							})
						);
					}
					this.runDefinitionChangeScope(targetSketch, definitionChangeOwnerId, () => {
						this.rollbackAttachedRelations(attachedRelations);
						this.objectCleanupService.cleanupTargetEntities(createdEntities);
					});
					return {
						createdReferences: [],
						createdReferenceIds: [],
						createdTargetEntityIds: [],
						diagnostics,
					};
				}

				const savedReference: SketchEntityReferenceRecord = {
					...preparedReferenceTarget.reference,
					projectionState: attachResult.record.projectionState,
				};
				createdReferences.push(savedReference);
				attachedRelations.push({
					reference: savedReference,
					targetEntity: preparedReferenceTarget.createdItem.targetEntity,
					record: attachResult.record,
				});
			}

			/**
			 * 普通创建在命令内完成一次参数求解；派生创建只准备实体和外部公式，
			 * 基础关系与完整目标草图求解由外层 Rebuild 原子批次统一处理。
			 */
			const solved = deferBatchConstraintResolution || this.runDefinitionChangeScope(
				targetSketch,
				definitionChangeOwnerId,
				() => this.dependencies.solveTargetSketchParametricState(input.targetSketchId),
			);
			if (!solved) {
				diagnostics.push(createExecutionDiagnostic({
					kind: 'relation-solve-failed',
					level: 'error',
					message: `转换实体引用创建后的统一参数求解失败: ${input.targetSketchId}`,
					referenceId: createdReferences[0]?.referenceId,
					targetEntityId: createdEntities[0]?.persistentId,
				}));
				this.runDefinitionChangeScope(targetSketch, definitionChangeOwnerId, () => {
					this.rollbackAttachedRelations(attachedRelations);
					this.objectCleanupService.cleanupTargetEntities(createdEntities);
				});
				return {
					createdReferences: [],
					createdReferenceIds: [],
					createdTargetEntityIds: [],
					diagnostics,
				};
			}

			if (!isInputCurrent()) {
				this.runDefinitionChangeScope(targetSketch, definitionChangeOwnerId, () => {
					this.rollbackAttachedRelations(attachedRelations);
					this.objectCleanupService.cleanupTargetEntities(createdEntities);
				});
				return this.createEmptyResult();
			}
			await executeSketchEntityReferenceRepositoryWrite(
				this.repository,
				repository,
				isInputCurrent,
				new Error('checkpoint 派生创建已过期'),
				async tx => {
					await tx.saveReferences(createdReferences);
				},
			);
			if (transaction) {
				transaction.record({
					targetSketchId: input.targetSketchId,
					batchKey,
					description: '回滚 checkpoint 派生创建实体',
					rollback: async () => {
						const rollbackDiagnostics: ExecutionDiagnostic[] = [];
						try {
							this.runDefinitionChangeScope(targetSketch, definitionChangeOwnerId, () => {
								targetSketch.runEntityReplacementScope(() => {
									this.rollbackAttachedRelations(attachedRelations);
									this.objectCleanupService.cleanupTargetEntities(createdEntities);
								});
							});
						} catch (error) {
							rollbackDiagnostics.push(createExecutionDiagnostic({
								kind: 'compensation-failed',
								level: 'error',
								message: `回滚派生创建运行时对象失败: ${error instanceof Error ? error.message : String(error)}`,
								targetSketchId: input.targetSketchId,
								referenceId: createdReferences[0]?.referenceId,
							}));
						}
						try {
							await repository.removeReferences(
								createdReferences.map(reference => reference.referenceId),
							);
						} catch (error) {
							rollbackDiagnostics.push(createExecutionDiagnostic({
								kind: 'compensation-failed',
								level: 'error',
								message: `回滚派生创建仓储记录失败: ${error instanceof Error ? error.message : String(error)}`,
								targetSketchId: input.targetSketchId,
								referenceId: createdReferences[0]?.referenceId,
							}));
						}
						return rollbackDiagnostics;
					},
				});
			}

			createdReferences.forEach(reference => {
				const targetEntity = createdEntities.find(
					entity => entity.persistentId === reference.targetEntityId,
				);
				if (!targetEntity) {
					return;
				}
				this.writeReferenceTag(
					targetEntity,
					{
						referenceId: reference.referenceId,
						isReferencedEntity: true,
					},
					diagnostics,
					reference.referenceId,
					reference.targetEntityId,
					runtime
				);
			});

			return {
				createdReferences: createdReferences.map(reference => structuredClone(reference)),
				createdReferenceIds: createdReferences.map(reference => reference.referenceId),
				createdTargetEntityIds: createdEntities.map(entity => entity.persistentId),
				diagnostics,
			};
		} catch (error) {
			this.runDefinitionChangeScope(targetSketch, definitionChangeOwnerId, () => {
				this.rollbackAttachedRelations(attachedRelations);
				this.objectCleanupService.cleanupTargetEntities(createdEntities);
			});
			return {
				createdReferences: [],
				createdReferenceIds: [],
				createdTargetEntityIds: [],
				diagnostics: [
					...diagnostics,
					createExecutionDiagnostic({
						kind: 'create-batch-failed',
						level: 'error',
						message: `创建几何引用命令执行失败: ${error instanceof Error ? error.message : String(error)}`,
					}),
				],
			};
		}
	}

	/**
	 * 创建派生任务已经过期时的无副作用结果。
	 *
	 * @returns 不包含新实体、引用或诊断的结果。
	 */
	private createEmptyResult(): CreateSketchEntityReferencesResult {
		return {
			createdReferences: [],
			createdReferenceIds: [],
			createdTargetEntityIds: [],
			diagnostics: [],
		};
	}

	/**
	 * 在可选的草图定义变更归属下执行同步几何操作。
	 *
	 * @param targetSketch 当前目标草图。
	 * @param ownerId 阶段 B owner；普通创建不提供。
	 * @param work 同步几何工作。
	 * @returns 工作结果。
	 */
	private runDefinitionChangeScope<T>(
		targetSketch: SketchGroup,
		ownerId: string | undefined,
		work: () => T,
	): T {
		return ownerId
			? targetSketch.runDefinitionChangeOwnerScope(ownerId, work)
			: work();
	}

	/**
	 * 回滚已挂载的引用关系。
	 *
	 * @param attachedRelations 已挂载关系集合。
	 */
	private rollbackAttachedRelations(
		attachedRelations: Array<{
			reference: SketchEntityReferenceRecord;
			targetEntity: Object3D;
			record: CreateGeometryReferencesAttachedRelationRecord;
		}>
	): void {
		[...attachedRelations].reverse().forEach(attachedRelation => {
			this.relationAttachDispatcher.detach({
				reference: attachedRelation.reference,
				targetEntity: attachedRelation.targetEntity,
			});
		});
	}

	/**
	 * 写入目标实体引用标记。
	 *
	 * @param targetObject 目标对象。
	 * @param tag 引用标记。
	 * @param diagnostics 诊断数组。
	 * @param referenceId 引用记录 id。
	 * @param targetEntityId 目标实体 id。
	 * @param runtime 运行时依赖。
	 */
	private writeReferenceTag(
		targetObject: Object3D,
		tag: SketchEntityReferenceTag,
		diagnostics: ExecutionDiagnostic[],
		referenceId: string,
		targetEntityId: string,
		runtime: CreateGeometryReferencesCommandServiceRuntime
	): void {
		try {
			if (runtime.writeReferenceTag) {
				runtime.writeReferenceTag(targetObject, tag);
			} else {
				writeSketchEntityReferenceTag(targetObject, tag);
			}
		} catch (error) {
			diagnostics.push(
				createExecutionDiagnostic({
					kind: 'tag-write-failed',
					level: 'error',
					message: error instanceof Error ? error.message : '写入转换实体引用标记失败。',
					referenceId,
					targetEntityId,
				})
			);
		}
	}
}
